import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Bell icon for the AppShell header. Polls the unread count every 60s.
 * Tap navigates to /notifications.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [unread, setUnread] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnread(data.unread_count || 0);
    } catch {}
  }, [api]);

  useEffect(() => {
    if (!user) return;
    refresh();
    timerRef.current = setInterval(refresh, 60000);
    return () => clearInterval(timerRef.current);
  }, [user, refresh]);

  // Refresh on focus so navigating back updates the badge
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  if (!user) return null;

  return (
    <button
      type="button"
      data-testid="notification-bell"
      onClick={() => navigate('/notifications')}
      className="relative w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center active:scale-95 transition-transform"
      aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
    >
      <Bell className="w-4 h-4 text-[#0B1620]" />
      {unread > 0 && (
        <span
          data-testid="notification-bell-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#F6F8FB]"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}
